import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faFile, faTimes, faPlus, faSave } from '@fortawesome/free-solid-svg-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { ItemListComponent, ListItem, ListConfig } from '../item-list/item-list.component';
import { ArchivesService, Archive } from '../../services/archives.service';
import { InteractionsService } from '../../services/interactions.service';
import { ModalUploadArchivesComponent } from '../modal-upload-archives/modal-upload-archives.component';
import { AlertService } from '../alert-service/alert-service';
import { UsersService, DEFAULT_MAX_SIMULATION_FILES } from '../../services/users.service';
import { forkJoin, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { PaginationComponent } from '../pagination/pagination.component';
import { SearchComponent, SearchConfig, SearchFilters } from '../search/search.component';
import { DatePipe, CommonModule } from '@angular/common';

@Component({
  selector: 'app-modal-interaction-archives-add',
  standalone: true,
  imports: [
    CommonModule,
    FontAwesomeModule,
    TranslateModule,
    FormsModule,
    ItemListComponent,
    ModalUploadArchivesComponent,
    PaginationComponent,
    SearchComponent
],
  providers: [DatePipe],
  templateUrl: './modal-interaction-archives-add.component.html',
  styleUrls: ['./modal-interaction-archives-add.component.scss']
})
export class ModalInteractionArchivesAddComponent implements OnInit, OnChanges {

    private countSelectedMatching(): number {
      if (!this.allMatchingIds) return 0;
      let count = 0;
      for (const id of this.allMatchingIds) {
        if (this.selectedArchives.has(id)) count++;
      }
      return count;
    }

    isAllFilteredSelected(): boolean {
      if (this.allMatchingIds) {
        if (!this.allMatchingIds.length) return false;
        const target = Math.min(this.allMatchingIds.length, this.MAX_ARCHIVES);
        return this.countSelectedMatching() >= target;
      }
      if (!this.filteredArchives.length) return false;
      return this.filteredArchives.every(a => this.selectedArchives.has(a.id));
    }

    isSomeFilteredSelected(): boolean {
      if (this.allMatchingIds) {
        return this.countSelectedMatching() > 0 && !this.isAllFilteredSelected();
      }
      if (!this.filteredArchives.length) return false;
      const selected = this.filteredArchives.filter(a => this.selectedArchives.has(a.id)).length;
      return selected > 0 && selected < this.filteredArchives.length;
    }

    private fetchAllMatchingIds(): Observable<(string | number)[]> {
      if (this.allMatchingIds) return of(this.allMatchingIds);

      const size = Math.max(this.totalArchives || this.pageSize, this.pageSize);
      return this.archivesService
        .list(this.selectedTag || undefined, this.searchTerm || undefined, 1, size, this.ext || undefined)
        .pipe(
          map(res => (res.archives || []).map(a => a.id)),
          tap(ids => { this.allMatchingIds = ids; })
        );
    }

    toggleSelectAllFiltered(checked: boolean) {
      if (this.selectingAll) return;
      this.selectingAll = true;

      this.fetchAllMatchingIds().subscribe({
        next: (ids) => {
          if (checked) {
            let reachedLimit = false;
            for (const id of ids) {
              if (this.selectedArchives.has(id)) continue;
              if (this.selectedArchives.size >= this.MAX_ARCHIVES) {
                reachedLimit = true;
                break;
              }
              this.selectedArchives.add(id);
            }
            if (reachedLimit) {
              this.alertService.showError(
                this.translate.instant('INTERACTIONS.MANAGE_FILES_MODAL.MAX_ARCHIVES_REACHED', { max: this.MAX_ARCHIVES })
              );
            }
          } else {
            ids.forEach(id => this.selectedArchives.delete(id));
          }
          this.selectingAll = false;
        },
        error: (err) => {
          this.selectingAll = false;
          this.alertService.showError(err);
        }
      });
    }
  @Input() show = false;
  @Input() interactionId: string = '';
  @Input() ext?: string;
  @Input() preselectedIds?: string[];
  @Input() emitOnly = false;
  @Output() close = new EventEmitter<void>();
  @Output() confirmed = new EventEmitter<string[]>();

  faFile = faFile;
  faTimes = faTimes;
  faPlus = faPlus;
  faSave = faSave;

  MAX_ARCHIVES = DEFAULT_MAX_SIMULATION_FILES;

  archives: Archive[] = [];
  interactionArchives: Archive[] = [];
  filteredArchives: ListItem[] = [];
  searchTerm: string = '';
  selectedTag: string = '';
  showUploadModal = false;
  selectedArchives: Set<string | number> = new Set();

  searchConfig: SearchConfig = {
    showTextSearch: true,
    showTagFilter: true,
    textPlaceholder: 'SEARCH.PLACEHOLDER.ARCHIVES',
    tagPlaceholder: 'SEARCH.PLACEHOLDER.TAGS'
  };
  
  currentPage: number = 1;
  pageSize: number = 20;
  totalArchives: number = 0;
  totalPages: number = 0;
  isLoading: boolean = false;
  selectingAll = false;
  private selectionInitialized = false;
  private allMatchingIds: (string | number)[] | null = null;

  listConfig: ListConfig = {
    itemIcon: faFile,
    itemIconClass: 'file-icon',
    showMetadata: true,
    showType: false,
    emptyMessage: 'MESSAGE.NO.ARCHIVES',
    emptyMessageFiltered: 'MESSAGE.NO.ARCHIVES',
    actions: [],
    showCheckboxes: true
  };

  constructor(
    private archivesService: ArchivesService,
    private interactionsService: InteractionsService,
    private alertService: AlertService,
    private usersService: UsersService,
    private datePipe: DatePipe,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.usersService.getMe().subscribe({
      next: (response) => {
        const maxFiles = response.user?.preferences?.max_simulation_files;
        this.MAX_ARCHIVES = maxFiles || DEFAULT_MAX_SIMULATION_FILES;
      },
      error: () => {
        this.MAX_ARCHIVES = DEFAULT_MAX_SIMULATION_FILES;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['show'] && this.show && (this.interactionId || this.emitOnly)) {
      this.searchTerm = '';
      this.selectedTag = '';
      this.currentPage = 1;
      this.selectionInitialized = false;
      this.allMatchingIds = null;
      this.loadData();
    }
  }

  loadData(idsToSelect?: string[]) {
    this.isLoading = true;
    forkJoin({
      allArchives: this.archivesService.list(this.selectedTag || undefined, this.searchTerm || undefined, this.currentPage, this.pageSize, this.ext || undefined),
      interactionArchives: this.interactionId ? this.interactionsService.listArchives(this.interactionId) : of([])
    }).subscribe({
      next: (data) => {
        this.archives = data.allArchives?.archives || [];
        this.totalArchives = data.allArchives?.total || 0;
        this.totalPages = data.allArchives?.total_pages || 0;
        this.interactionArchives = data.interactionArchives || [];

        if (!this.selectionInitialized) {
          this.preselectInteractionArchives();
          this.selectionInitialized = true;
        }

        if (idsToSelect && idsToSelect.length > 0) {
          idsToSelect.forEach(id => {
            if (this.selectedArchives.size < this.MAX_ARCHIVES) {
              const numericId = !isNaN(Number(id)) ? Number(id) : id;
              this.selectedArchives.add(numericId);
              this.selectedArchives.add(String(id));
            }
          });
        }

        this.applyFilter();
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.archives = [];
        this.interactionArchives = [];
        this.filteredArchives = [];
        this.alertService.showError(err);
      }
    });
}

  preselectInteractionArchives() {
    this.selectedArchives.clear();
    if (this.interactionId) {
      this.interactionArchives.forEach(archive => {
        this.selectedArchives.add(archive.id);
      });
    } else {
      (this.preselectedIds || []).forEach(id => this.selectedArchives.add(id));
    }
  }

  onAddArchive(): void {
    this.showUploadModal = true;
  }

  closeUploadModal(): void {
    this.showUploadModal = false;
  }

  onUploadSuccess(uploadedIds: string[]): void {
    this.allMatchingIds = null;
    this.loadData(uploadedIds);
  }

  onFiltersChanged(filters: SearchFilters) {
    this.searchTerm = filters.searchTerm || '';
    this.selectedTag = filters.selectedTag || '';
    this.currentPage = 1;
    this.allMatchingIds = null;
    this.loadData();
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.loadData();
  }

  applyFilter() {
    const toItem = (a: Archive) => ({
      id: a.id,
      name: a.name,
      tag: a.tag,
      tags: a.tags || [],
      created_date: a.created_at ? this.datePipe.transform(a.created_at, 'dd/MM/yyyy') || '' : '',
      created_time: a.created_at ? this.datePipe.transform(a.created_at, 'HH:mm') || '' : '',
    });

    this.filteredArchives = this.archives.map(toItem);
  }

  onClose() {
    this.close.emit();
  }

  onItemSelectionChanged(event: { item: ListItem; selected: boolean }) {
    if (event.selected) {
      if (this.selectedArchives.size >= this.MAX_ARCHIVES) {
        this.alertService.showError(
          this.translate.instant('INTERACTIONS.MANAGE_FILES_MODAL.MAX_ARCHIVES_REACHED', { max: this.MAX_ARCHIVES })
        );
        return;
      }
      this.selectedArchives.add(event.item.id);
    } else {
      this.selectedArchives.delete(event.item.id);
    }
  }

  addSelectedArchivesToInteraction() {
    const archiveIds = Array.from(this.selectedArchives).map(id => String(id));

    if (this.emitOnly) {
      this.confirmed.emit(archiveIds);
      this.close.emit();
      return;
    }

    if (archiveIds.length === 0) return;

    this.interactionsService.updateArchives(this.interactionId, archiveIds).subscribe({
      next: (response) => {
        this.alertService.showSuccess('INTERACTIONS.ARCHIVES_UPDATE_SUCCESS');
        this.close.emit();
      },
      error: (err) => {
        this.alertService.showError(err);
      }
    });
  }

  get hasSelectedArchives(): boolean {
    return this.selectedArchives.size > 0;
  }

  get selectedArchivesCount(): number {
    return this.selectedArchives.size;
  }

  get isAtMaxArchives(): boolean {
    return this.selectedArchives.size >= this.MAX_ARCHIVES;
  }
}